<!-- Hook dialog (#vaultEditModal, data in data.vault): new or existing hook. Fields in order: project (with at least
     two projects; move, or copy to another project, for a saved hook), title (required), tags of the chosen project
     (several; "+" creates one there), content with @mentions. A new hook starts with the tag chosen on the Hooks page
     (TAGSTATE.hook.filter) and the project of new items; text dropped on the Hooks page fills title and content
     (prefill, src/stores/drop.ts). Saving a new hook shows the thumbs-up "Hook added"; delete asks first.
     Opened with openVaultModal(id?, prefill?) (create button, Hooks page, search, mentions, drop) through the
     dialogs store; logic in src/features/hooks.ts. Like the legacy dialog, it closes only with ‹, × or the buttons; Enter in the title, Ctrl/Cmd+S and Ctrl/Cmd+Enter save. -->
<script setup lang="ts">
import EditDialogFooter from '../fields/EditDialogFooter.vue'
import { requiredCls } from '../../utils/fieldCls'
import { nextTick, onMounted, reactive, ref, watch } from 'vue'
import AppDialog from '../AppDialog.vue'
import ProjectPicker from '../fields/ProjectPicker.vue'
import TagPicker from '../fields/TagPicker.vue'
import MentionField from '../fields/MentionField.vue'
import { useI18n } from '../../composables/useI18n'
import { useDataStore } from '../../stores/data'
import { useTagStore } from '../../stores/tags'
import type { HookPrefill } from '../../stores/dialogs'
import { deleteHook, hookForm, saveHook } from '../../features/hooks'
import { validProject } from '../../features/projects'
import { newItemProject } from '../../stores/projects'
import { confirmDelete, uiAlertThen, uiSuccess } from '../../stores/notices'
import { enterKey } from '../../features/dialogs'
import { finePointer } from '../../utils/media'
import { saveAppData } from '../../features/storage/save'

const props = defineProps<{ id: string | null; prefill?: HookPrefill }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const store = useDataStore()

const form = reactive(hookForm(store.getData(), props.id, useTagStore().state.hook.filter, newItemProject()))
if (props.prefill) {
  if (props.prefill.title !== undefined) form.title = props.prefill.title
  if (props.prefill.content !== undefined) form.content = props.prefill.content
}

const titleInput = ref<HTMLInputElement | null>(null)
const tagPicker = ref<InstanceType<typeof TagPicker> | null>(null)
// Dropped text: the title is ready to be retyped; a new hook starts in the title (with a mouse: no phone keyboard over the dialog)
onMounted(() => {
  if (props.prefill) nextTick(() => { titleInput.value?.focus(); titleInput.value?.select() })
  else if (!props.id && finePointer()) nextTick(() => titleInput.value?.focus())
})
// The title is required: shown in the main colour, with the cursor in it, after the message
const missing = ref(false)
watch(() => form.title, v => { if (v.trim()) missing.value = false })
const onEnter = enterKey(save)

function save() {
  if (!form.title.trim()) { missing.value = true; void uiAlertThen(t('Enter at least a title for the hook!')).then(() => titleInput.value?.focus()); return }
  const tags = tagPicker.value ? tagPicker.value.flush() : form.tags
  const d = store.getData()
  const r = saveHook(d, props.id, { ...form, tags, projectId: validProject(d, form.projectId, newItemProject()) })
  if (!r) return
  if (r.created) uiSuccess(t('Hook added'))
  saveAppData(d)
  emit('close')
}

async function remove() {
  if (!props.id) return
  if (!(await confirmDelete(t('Are you sure you want to delete this hook?')))) return
  const d = store.getData()
  deleteHook(d, props.id)
  saveAppData(d)
  emit('close')
}
</script>

<template>
  <AppDialog id="vaultEditModal" name="hook" title-id="vaultModalTitle" :title="id ? t('Edit hook') : t('New hook')" editing :save="save" @close="emit('close')">
    <ProjectPicker v-model="form.projectId" kind="hook" :item-id="id" />
    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('TITLE') }}</label>
      <input id="inputVaultTitle" ref="titleInput" v-model="form.title" type="text" :placeholder="t('E.g. Hook for a Short about Nintendo')"
        :class="requiredCls(missing)" @keydown.enter="onEnter">
    </div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider"><span>{{ t('TAGS') }}</span> <span class="font-normal text-gray-500 tracking-normal">{{ t('(one or more)') }}</span></label>
      <TagPicker id="hookTagPicker" ref="tagPicker" v-model="form.tags" kind="hook" :project-id="form.projectId" />
    </div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('CONTENT / HOOK') }}</label>
      <MentionField id="inputVaultContent" v-model="form.content" :placeholder="t('Write your idea, hook or story hook here... (@ to mention)')"
        class="min-h-[7.5rem] w-full bg-darkinput border border-gray-800 rounded-lg p-4 text-gray-100 focus:outline-none focus:border-brand transition" />
    </div>

    <template #footer>
      <EditDialogFooter delete-id="deleteVaultBtn" :can-delete="!!id" :delete-label="t('Delete hook')" :save-label="t('Save hook')" @delete="remove" @save="save" />
    </template>
  </AppDialog>
</template>
